// Radha — the four smaller global-desk surfaces (075-078), each mounted
// inside an existing tab rather than claiming its own:
//   SettlementOutlook  -> Cashflow    (when promised money actually LANDS)
//   LimitationWatch    -> Dunning     (deadlines feed escalation triage)
//   DebtorDnaPanel     -> Collections (who you are actually dealing with)
//   GroupExposurePanel -> Statements  (counterparty truth across entities)
import React from "react";

import {
  useSettlementOutlook, useLimitationWatchlist, useDebtorDna, useGroupExposure,
} from "../hooks/useGlobalDesk";
import { fmtMoney, fmtMoneyFull, fmtDate } from "../lib/money";
import { useMe } from "../lib/authSupabase";
import { useViewCountry } from "../lib/jurisdiction";

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "8px" };
const num: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const headRow: React.CSSProperties = { textAlign: "left", color: "var(--fg-muted)", fontSize: 11.5 };

// ---- G2 --------------------------------------------------------------------
export function SettlementOutlook() {
  const me = useMe(true);
  const viewCountry = useViewCountry(me?.tenant_country);
  const { data } = useSettlementOutlook(viewCountry);
  const rows = data ?? [];
  if (rows.length === 0) return null;
  const landing = rows.reduce((s, r) => s + Number(r.promised_amount), 0);

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", marginBottom: 22 }}>
      <div style={{ font: "600 15px Inter", marginBottom: 4 }}>Settlement outlook</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 10px" }}>
        Promises projected to the day the money should <em>land</em>, not the day it is sent — rail speed, weekends
        and bank holidays included. {fmtMoney(landing)} promised across {rows.length} commitments.
      </p>
      <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={headRow}>
            <th style={th}>Customer</th><th style={{ ...th, textAlign: "right" }}>Promised</th>
            <th style={th}>Send date</th><th style={th}>Rail</th><th style={th}>Expected landing</th><th style={th}>Slip</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.promise_id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{r.customer_name}</td>
                <td style={num}>{fmtMoney(r.promised_amount)}</td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{fmtDate(r.promised_date)}</td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{r.rail_label}</td>
                <td style={{ ...td, fontWeight: r.slip_days > 0 ? 600 : 400 }}>{fmtDate(r.expected_landing)}</td>
                <td style={{ ...td, color: r.slip_days > 0 ? "var(--status-warning)" : "var(--fg-faint)", fontSize: 12 }}>
                  {r.slip_days > 0 ? `+${r.slip_days}d` : "same day"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- G3 --------------------------------------------------------------------
export function LimitationWatch() {
  const me = useMe(true);
  const viewCountry = useViewCountry(me?.tenant_country);
  const { data } = useLimitationWatchlist(viewCountry);
  const rows = (data ?? []).slice(0, 10);
  if (rows.length === 0) return null;
  const first = data![0];
  const urgency = (d: number) =>
    d < 180 ? "var(--status-critical)" : d < 365 ? "var(--status-warning)" : "var(--fg-muted)";

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", margin: "22px 0" }}>
      <div style={{ font: "600 15px Inter", marginBottom: 4 }}>Limitation clock</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 10px", maxWidth: 720 }}>
        {first.rule_years}-year limitation ({first.statute_ref}) — sue after expiry and the claim is time-barred
        regardless of merit. A part-payment restarts the clock, so the anchor shown is the later of due date and
        last payment. The ten soonest expiries of {data!.length} open invoices:
        {!first.verified && <strong style={{ color: "var(--status-warning)" }}> rule unverified — confirm with counsel.</strong>}
      </p>
      <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={headRow}>
            <th style={th}>Invoice</th><th style={th}>Customer</th>
            <th style={{ ...th, textAlign: "right" }}>Outstanding</th>
            <th style={th}>Clock runs from</th><th style={th}>Expires</th>
            <th style={{ ...th, textAlign: "right" }}>Days left</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.invoice_id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={{ ...td, fontSize: 12 }}>{r.invoice_number}</td>
                <td style={td}>{r.customer_name}</td>
                <td style={num}>{fmtMoney(r.outstanding, r.currency)}</td>
                <td style={{ ...td, color: "var(--fg-muted)", fontSize: 12 }}>{fmtDate(r.anchor_date)} <span style={{ color: "var(--fg-faint)" }}>({r.anchor_source})</span></td>
                <td style={{ ...td, color: "var(--fg-muted)" }}>{fmtDate(r.expiry_date)}</td>
                <td style={{ ...num, fontWeight: 600, color: urgency(r.days_left) }}>{r.days_left}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- G4 --------------------------------------------------------------------
export function DebtorDnaPanel() {
  const { data } = useDebtorDna();
  const rows = (data ?? []).filter((r) => Number(r.outstanding) > 0).slice(0, 12);
  if (rows.length === 0) return null;

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", margin: "22px 0" }}>
      <div style={{ font: "600 15px Inter", marginBottom: 4 }}>Payment behaviour</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 10px", maxWidth: 720 }}>
        Each debtor's observable fingerprint, computed live from settled history. A customer who reliably pays at
        DPD+20 does not need a day-5 nudge; one who deducts on every invoice needs a different conversation
        entirely. n is the number of settled invoices behind the estimate — treat thin history as thin.
      </p>
      <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={headRow}>
            <th style={th}>Customer</th>
            <th style={{ ...th, textAlign: "right" }}>Outstanding</th>
            <th style={th}>Pays at</th><th style={th}>Pay day</th>
            <th style={th}>Deducts</th><th style={th}>Promises broken</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const lag = r.avg_lag_days == null ? null : Number(r.avg_lag_days);
              return (
                <tr key={r.customer_name} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={td}>{r.customer_name}</td>
                  <td style={num}>{fmtMoney(r.outstanding)}</td>
                  <td style={td}>
                    {lag == null ? <span style={{ color: "var(--fg-faint)" }}>no history</span> : (
                      <span style={{ color: lag > 15 ? "var(--status-warning)" : "var(--status-good)", fontWeight: 600 }}>
                        {lag >= 0 ? `DPD+${lag}` : `${-lag}d early`}
                        <span style={{ color: "var(--fg-faint)", fontWeight: 400 }}> ±{Number(r.lag_stddev ?? 0)} · n={r.paid_invoices}</span>
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, color: "var(--fg-muted)" }}>{r.typical_pay_dom ? `~${r.typical_pay_dom}th` : "—"}</td>
                  <td style={td}>
                    {r.deduction_count > 0
                      ? <span style={{ color: "var(--status-warning)" }}>{r.deduction_count}× · {Number(r.deduction_rate_pct)}% of billings</span>
                      : <span style={{ color: "var(--fg-faint)" }}>clean</span>}
                  </td>
                  <td style={{ ...td, color: r.broken_promises > 0 ? "var(--status-critical)" : "var(--fg-faint)" }}>
                    {r.broken_promises > 0 ? r.broken_promises : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- G5 --------------------------------------------------------------------
export function GroupExposurePanel() {
  const { data } = useGroupExposure();
  const rows = (data ?? []).slice(0, 12);
  if (rows.length === 0) return null;
  const offsetable = rows.filter((r) => Number(r.open_credit_notes) > 0);

  return (
    <div className="sanctuary-card" style={{ padding: "14px 16px", margin: "22px 0" }}>
      <div style={{ font: "600 15px Inter", marginBottom: 4 }}>Counterparty position</div>
      <p style={{ color: "var(--fg-muted)", fontSize: 12, margin: "0 0 10px", maxWidth: 760 }}>
        The true position per counterparty across entities and currencies: gross receivable, less our own paper in
        their favour (open credit notes) — which is the amount a demand letter should actually claim. Radha holds no
        payables ledger, so this nets what it can see, and says so.
        {offsetable.length > 0 && <strong> {offsetable.length} counterparties have offsets worth proposing.</strong>}
      </p>
      <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr style={headRow}>
            <th style={th}>Counterparty</th><th style={th}>Entities</th>
            <th style={{ ...th, textAlign: "right" }}>Gross receivable</th>
            <th style={{ ...th, textAlign: "right" }}>Our credit notes</th>
            <th style={{ ...th, textAlign: "right" }}>Open deductions</th>
            <th style={{ ...th, textAlign: "right" }}>Net claim</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.customer_name} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{r.customer_name}
                  {r.currencies.includes(",") && <span style={{ marginInlineStart: 6, fontSize: 10, color: "var(--fg-faint)" }}>{r.currencies}</span>}
                </td>
                <td style={{ ...td, color: "var(--fg-muted)", fontSize: 12 }}>{r.entity_count > 1 ? `${r.entity_count} · ${r.entities}` : r.entities}</td>
                <td style={num}>{fmtMoney(r.gross_receivable)}</td>
                <td style={{ ...num, color: Number(r.open_credit_notes) > 0 ? "var(--status-warning)" : "var(--fg-faint)" }}>
                  {Number(r.open_credit_notes) > 0 ? `−${fmtMoneyFull(r.open_credit_notes)}` : "—"}
                </td>
                <td style={{ ...num, color: "var(--fg-muted)" }}>{Number(r.open_deductions) > 0 ? fmtMoney(r.open_deductions) : "—"}</td>
                <td style={{ ...num, fontWeight: 600 }}>{fmtMoney(r.net_position)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
